import { bool, node, number, object, oneOfType, shape } from "prop-types";
import { forwardRef, useCallback, useEffect, useRef, useState } from "react";

import styled from "styled-components/macro";

import FnrOpacityToggle from "UI/FnrOpacityToggle";
import { marketingColors } from "UI/colors";

import useIsHoverFocused from "Utilities/Hooks/useIsHoverFocused";
import useResizeObserver2 from "Utilities/Hooks/useResizeObserver2";

const SCROLLBAR_WIDTH = 10;
const MIN_THUMB_HEIGHT = 20;

const Container = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const ContentWrapper = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  overflow-x: ${({ $clipX }) => ($clipX ? "clip" : "hidden")};

  /* Hide native scrollbar */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
  }
`;

const InnerContentWrapper = styled.div`
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;

  /* border-box so a gutter is taken out of the content's own width instead of
     pushing it past the scroller. Inert at the default gutter of none. */
  box-sizing: border-box;
  padding-right: ${({ $gutter }) => $gutter}px;
`;

const ScrollbarTrack = styled.div`
  position: absolute;
  right: 0;
  top: ${({ $trackTop }) => $trackTop}px;
  bottom: ${({ $trackBottom }) => $trackBottom}px;
  width: ${SCROLLBAR_WIDTH}px;
  background: ${({ $trackColor }) => $trackColor};
  border-radius: 4px;
  pointer-events: auto;
  z-index: 10;
`;

const ScrollbarThumb = styled.div`
  position: absolute;
  right: 0;
  width: ${SCROLLBAR_WIDTH}px;
  background-color: ${({ $thumbColor }) => $thumbColor};
  border-radius: 5px;
  cursor: pointer;
  transition: opacity 300ms;
  opacity: 0.4;

  &:hover {
    opacity: 0.9;
  }

  ${({ $isDragging }) =>
    $isDragging &&
    `
    opacity: 0.9;
    transition: none;
  `}
`;

const CustomScrollbar = forwardRef(
  /**
   * A custom overlay scrollbar that doesn't take up content space
   * @param {object} props
   * @param {node} props.children - Content to be scrolled
   * @param {object|string} props.colors - Scrollbar colors { color: thumbColor, backgroundColor: trackColor }
   * @param {boolean} props.clipX - Whether to clip horizontal overflow
   * @param {number|object} props.trackInset - Inset for scrollbar track. Number for equal top/bottom, or { top, bottom }
   * @param {boolean} props.alwaysShow - Keep the bar up for as long as the content overflows, instead of revealing it on hover/focus. For content laid out with a gutter for the bar: revealed on hover it arrives on top of whatever the pointer is already over, and there is no hover state to arrive over if it was there all along.
   * @param {number} props.contentGap - Px of clearance to leave between the content and the bar; the bar's own width is added on top. Only while the bar is up, so content that fits keeps its full width — which is why this lives here and not on the content, where nothing knows whether the bar came.
   * @param {import("react").Ref} ref - Forwarded ref to the scrollable content wrapper
   *
   * @example
   * // Height must be set by the consumer via styled-components
   * const MyScrollbar = styled(CustomScrollbar)`
   *   height: 100vh;
   * `;
   */
  (
    { children, colors, clipX, trackInset, alwaysShow, contentGap, ...rest },
    ref
  ) => {
    const localRef = useRef(null);
    const contentRef = ref || localRef;
    const innerRef = useRef(null);
    const thumbRef = useRef(null);
    const trackRef = useRef(null);
    const isContentHoverFocused = useIsHoverFocused(
      contentRef,
      trackRef,
      thumbRef
    );

    const [thumbHeight, setThumbHeight] = useState(0);
    const [thumbTop, setThumbTop] = useState(0);
    const [isDragging, setIsDragging] = useState(false);
    const [showScrollbar, setShowScrollbar] = useState(false);

    const dragStartY = useRef(0);
    const dragStartScrollTop = useRef(0);

    // Extract colors
    const thumbColor =
      typeof colors === "object"
        ? colors.color || marketingColors.taupe
        : marketingColors.taupe;
    const trackColor =
      typeof colors === "object"
        ? colors.backgroundColor || marketingColors.grey
        : marketingColors.grey;

    // Extract track inset
    const trackTop =
      typeof trackInset === "number" ? trackInset : trackInset?.top ?? 0;
    const trackBottom =
      typeof trackInset === "number" ? trackInset : trackInset?.bottom ?? 0;

    // Calculate thumb dimensions and position
    const updateThumb = useCallback(() => {
      if (!contentRef.current) return;

      const { scrollHeight, clientHeight, scrollTop } = contentRef.current;

      // Check if content overflows (doesn't need trackRef)
      if (scrollHeight <= clientHeight) {
        setShowScrollbar(false);
        return;
      }

      setShowScrollbar(true);

      // Only calculate thumb position if track exists
      if (!trackRef.current) return;

      const trackHeight = trackRef.current.clientHeight;

      // Calculate thumb height
      const calculatedThumbHeight = Math.max(
        (clientHeight / scrollHeight) * trackHeight,
        MIN_THUMB_HEIGHT
      );
      setThumbHeight(calculatedThumbHeight);

      // Calculate thumb position
      const maxScrollTop = scrollHeight - clientHeight;
      const maxThumbTop = trackHeight - calculatedThumbHeight;
      const calculatedThumbTop = (scrollTop / maxScrollTop) * maxThumbTop;
      setThumbTop(calculatedThumbTop);
    }, [contentRef]);

    // Handle scroll events
    const handleScroll = useCallback(() => {
      updateThumb();
    }, [updateThumb]);

    // Handle thumb drag start
    const handleThumbMouseDown = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
        dragStartY.current = e.clientY;
        dragStartScrollTop.current = contentRef.current.scrollTop;
      },
      [contentRef]
    );

    // Handle mouse move during drag
    const handleMouseMove = useCallback(
      (e) => {
        if (!isDragging || !contentRef.current || !trackRef.current) return;

        const deltaY = e.clientY - dragStartY.current;
        const { scrollHeight, clientHeight } = contentRef.current;
        const trackHeight = trackRef.current.clientHeight;
        const maxScrollTop = scrollHeight - clientHeight;
        const maxThumbTop = trackHeight - thumbHeight;

        // Calculate new scroll position
        const scrollRatio = deltaY / maxThumbTop;
        const newScrollTop =
          dragStartScrollTop.current + scrollRatio * maxScrollTop;

        contentRef.current.scrollTop = Math.max(
          0,
          Math.min(newScrollTop, maxScrollTop)
        );
      },
      [isDragging, thumbHeight, contentRef]
    );

    // Handle mouse up to end drag
    const handleMouseUp = useCallback(() => {
      setIsDragging(false);
    }, []);

    // Handle track click for jump scrolling
    const handleTrackClick = useCallback(
      (e) => {
        if (
          !contentRef.current ||
          !trackRef.current ||
          e.target !== trackRef.current
        )
          return;

        const trackRect = trackRef.current.getBoundingClientRect();
        const clickY = e.clientY - trackRect.top;
        const { scrollHeight, clientHeight } = contentRef.current;
        const trackHeight = trackRef.current.clientHeight;

        // Calculate target scroll position
        const scrollRatio =
          (clickY - thumbHeight / 2) / (trackHeight - thumbHeight);
        const maxScrollTop = scrollHeight - clientHeight;
        const targetScrollTop = scrollRatio * maxScrollTop;

        contentRef.current.scrollTop = Math.max(
          0,
          Math.min(targetScrollTop, maxScrollTop)
        );
      },
      [thumbHeight, contentRef]
    );

    // Set up event listeners
    useEffect(() => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
        };
      }
    }, [isDragging, handleMouseMove, handleMouseUp]);

    // Update thumb when container or content size changes
    useResizeObserver2(contentRef, updateThumb);
    useResizeObserver2(innerRef, updateThumb);

    return (
      <Container>
        <ContentWrapper ref={contentRef} onScroll={handleScroll} $clipX={clipX}>
          <InnerContentWrapper
            ref={innerRef}
            $gutter={
              showScrollbar && contentGap != null
                ? SCROLLBAR_WIDTH + contentGap
                : 0
            }
            {...rest}
          >
            {children}
          </InnerContentWrapper>
        </ContentWrapper>
        <FnrOpacityToggle
          data-cy="scrollbar-visibility"
          show={
            showScrollbar &&
            !!(alwaysShow || isContentHoverFocused || isDragging)
          }
        >
          <ScrollbarTrack
            ref={trackRef}
            $trackColor={trackColor}
            $trackTop={trackTop}
            $trackBottom={trackBottom}
            onClick={handleTrackClick}
            data-cy="scrollbar-track"
          >
            <ScrollbarThumb
              ref={thumbRef}
              $thumbColor={thumbColor}
              $trackColor={trackColor}
              $isDragging={isDragging}
              onMouseDown={handleThumbMouseDown}
              style={{
                height: `${thumbHeight}px`,
                top: `${thumbTop}px`,
              }}
            />
          </ScrollbarTrack>
        </FnrOpacityToggle>
      </Container>
    );
  }
);

CustomScrollbar.propTypes = {
  children: node,
  colors: object,
  clipX: bool,
  alwaysShow: bool,
  contentGap: number,
  trackInset: oneOfType([
    number,
    shape({
      top: number,
      bottom: number,
    }),
  ]),
};

export default CustomScrollbar;
